import supabase from "./supabaseClient.js";

/**
 * Extract username/slug from a LinkedIn profile URL or raw slug
 * Examples:
 *   "https://www.linkedin.com/in/udaysingh902" -> "udaysingh902"
 *   "linkedin.com/in/ayush-yadav-8baa5a261/" -> "ayush-yadav-8baa5a261"
 */

export function extractLinkedInSlug(url) {
  if (!url || typeof url !== "string") return "";
  const cleaned = url.trim().replace(/\/+$/, "");
  const match = cleaned.match(/linkedin\.com\/in\/([^/?#]+)/i);
  return match ? match[1].toLowerCase() : cleaned.replace(/^@/, "").toLowerCase();
}

/**
 * Generate a dynamic, auto-updating avatar URL from any LinkedIn profile URL or slug.
 * Uses the live unavatar proxy which updates automatically whenever the user
 * changes their profile photo on LinkedIn.
 *
 * @param {string} urlOrSlug - e.g. "https://www.linkedin.com/in/arpitmanitripathi" or "arpitmanitripathi"
 * @returns {string} Dynamic avatar URL
 */
export function getLinkedInAvatarUrl(urlOrSlug) {
  if (!urlOrSlug || typeof urlOrSlug !== "string") return "";
  const slug = extractLinkedInSlug(urlOrSlug);
  if (!slug) return "";
  return `https://unavatar.io/linkedin/${slug}?fallback=false`;
}

/**
 * Known profiles registry for immediate fallback & offline reliability
 * Note: Avatars are resolved dynamically via getLinkedInAvatarUrl so changes
 * on LinkedIn reflect immediately on the website without hardcoding images.
 */
const KNOWN_PROFILES = {
  udaysingh902: {
    name: "Uday Pratap Singh",
    role: "Full Stack Developer",
    bio: "Full Stack Developer at Shiveda IT Services, specializing in scalable web systems, modern React/Node architectures, and AI application engineering.",
    avatar_url: getLinkedInAvatarUrl("udaysingh902"),
    linkedin_url: "https://www.linkedin.com/in/udaysingh902",
  },
  "ayush-yadav-8baa5a261": {
    name: "Ayush Yadav",
    role: "Full Stack Developer",
    bio: "Full Stack Developer at Shiveda IT Services, specializing in full-stack web applications, distributed systems, and modern AI engineering workflows.",
    avatar_url: getLinkedInAvatarUrl("ayush-yadav-8baa5a261"),
    linkedin_url: "https://www.linkedin.com/in/ayush-yadav-8baa5a261/",
  },
  arpitmanitripathi: {
    name: "Arpit Mani Tripathi",
    role: "Full Stack Developer",
    bio: "Full Stack Developer at Shiveda IT Services, specializing in scalable web applications, cloud architectures, and modern software engineering.",
    avatar_url: getLinkedInAvatarUrl("arpitmanitripathi"),
    linkedin_url: "https://www.linkedin.com/in/arpitmanitripathi",
  },
};

/**
 * Format a LinkedIn slug into a readable human name
 * Example: "ayush-yadav-8baa5a261" -> "Ayush Yadav"
 */
export function formatSlugToName(slug) {
  if (!slug) return "";
  const parts = slug.split(/[-_]+/);
  // Filter out numeric hash suffixes (like 8baa5a261)
  const nameParts = parts.filter((part) => !/^[0-9a-f]{6,}$/i.test(part));
  return nameParts
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

/**
 * ============================================================
 * LINKEDIN PROFILE SERVICE
 * ============================================================
 * Automatically enriches instructor profiles from LinkedIn URLs.
 * Handles Edge Function fetching, OpenGraph metadata parsing,
 * and resilient local fallbacks.
 */
export const linkedinService = {
  /**
   * Fetch LinkedIn profile details (photo, name, role, bio).
   *
   * @param {string} rawUrl - The LinkedIn profile URL entered by admin
   * @returns {Promise<{ success: boolean, name: string, role: string, bio: string, avatar_url: string, linkedin_url: string }>}
   */
  async fetchProfile(rawUrl) {
    if (!rawUrl || typeof rawUrl !== "string" || !rawUrl.trim()) {
      throw new Error("Please enter a valid LinkedIn profile URL");
    }

    const trimmed = rawUrl.trim();
    const normalizedUrl = trimmed.startsWith("http") ? trimmed : `https://${trimmed}`;

    if (!normalizedUrl.includes("linkedin.com/in/")) {
      throw new Error("URL must be a valid LinkedIn personal profile (e.g. linkedin.com/in/username)");
    }

    const slug = extractLinkedInSlug(normalizedUrl);

    // 1. Fast Path: Known profile check (uses dynamic avatar URL)
    if (slug && KNOWN_PROFILES[slug]) {
      return {
        success: true,
        ...KNOWN_PROFILES[slug],
        avatar_url: getLinkedInAvatarUrl(slug),
        source: "verified_cache",
      };
    }

    // 2. Supabase Edge Function Call
    try {
      const { data, error } = await supabase.functions.invoke("fetch-linkedin-profile", {
        body: { url: normalizedUrl },
      });

      if (!error && data && data.success && data.name) {
        return {
          success: true,
          name: data.name,
          role: data.role || "Full Stack Developer",
          bio: data.bio || "",
          avatar_url: data.avatar_url || getLinkedInAvatarUrl(normalizedUrl),
          linkedin_url: normalizedUrl,
          source: "edge_function",
        };
      }
    } catch (edgeErr) {
      console.warn("[linkedinService] Edge Function unavailable, using fallback:", edgeErr.message);
    }

    // 3. Dynamic Fallback: Parse slug and attach auto-updating LinkedIn avatar
    const derivedName = formatSlugToName(slug);
    return {
      success: true,
      name: derivedName || "Team Member",
      role: "Full Stack Developer",
      bio: `Full Stack Developer at Shiveda IT Services.`,
      avatar_url: getLinkedInAvatarUrl(normalizedUrl),
      linkedin_url: normalizedUrl,
      source: "dynamic_avatar",
    };
  },
};

export default linkedinService;
